import { WikiPage } from '../../types/wiki';
import { SemanticTone, statusTone } from '../ui/colors';

/** Labels for wiki statuses; tones come from the shared status palette */
export const WIKI_STATUS: Record<WikiPage['status'], { label: string; tone: SemanticTone }> = {
  approved: { label: 'Published', tone: statusTone('approved') },
  pending: { label: 'In review', tone: statusTone('pending') },
  draft: { label: 'Draft', tone: statusTone('draft') },
  rejected: { label: 'Rejected', tone: statusTone('rejected') },
};

/** Short relative time: "just now", "5m ago", "3h ago", "2d ago", then a date. */
export function timeAgo(iso?: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  const time = date.getTime();
  if (Number.isNaN(time)) return '';
  const minutes = Math.round((Date.now() - time) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) });
}

export function formatDateTime(iso?: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function personName(
  person?: { firstName?: string; lastName?: string; email?: string } | null,
  fallback = '',
): string {
  const name = `${person?.firstName || ''} ${person?.lastName || ''}`.trim();
  return name || person?.email || fallback;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Author ids are not worth showing; emails and names are. */
export const readableAuthor = (value?: string | null): string => (value && !UUID.test(value) ? value : '');

/** Wiki content is HTML from the rich editor; older pages may be Markdown. */
export const looksLikeHtml = (value: string): boolean => /<\/?[a-z][\s\S]*>/i.test(value);
